/**
 * Busca pública das bases ativas para o combo da apex central.
 *
 * Endpoint: GET /api/tenants/active
 * Contrato: { tenants: PublicTenantInfo[] }
 *  - Sempre 200 (mesmo lista vazia); Cache-Control: public, max-age=60
 *  - Backend já ordena por `name` (pt-BR) e filtra apenas status = ACTIVE
 *  - `loginUrl` vem pronto (PUBLIC_TENANT_URL_PATTERN) — o front só redireciona
 *
 * ATENÇÃO (backend): a rota precisa ser GLOBAL (sem resolução de tenant por
 * subdomínio). Nas apex centrais (escolarai.com.br / front-v2...) uma rota
 * tenant-scoped retorna 401 ("Tenant \"escolarai\" não registrado"). Enquanto
 * isso, o CentralBaseSelector cai no fallback de `@/config/bases`.
 */
import { buildRestApiUrl } from "@/lib/config";

export interface PublicTenantInfo {
  /** Identificador do tenant (subdomínio em prod). */
  slug: string;
  /** Nome completo (tenants.name). */
  name: string;
  /** Nome curto para botões/cards (opcional). */
  shortName?: string;
  /** URL do logo (opcional). */
  logoUrl?: string;
  /** Cor primária em hex (opcional). */
  primaryColor?: string;
  /** URL pronta para redirecionar (já no host correto do ambiente). */
  loginUrl: string;
}

interface ListTenantsResponse {
  tenants: PublicTenantInfo[];
}

/**
 * Retorna as bases ativas. Lança em caso de erro de rede/HTTP — o chamador
 * decide o fallback.
 */
export async function fetchActiveTenants(
  signal?: AbortSignal,
): Promise<PublicTenantInfo[]> {
  const url = buildRestApiUrl("api/tenants/active");
  const res = await fetch(url, {
    headers: { Accept: "application/json" },
    signal,
  });
  if (!res.ok) {
    throw new Error(`GET /api/tenants/active -> ${res.status}`);
  }
  const data = (await res.json()) as ListTenantsResponse;
  return Array.isArray(data?.tenants) ? data.tenants : [];
}
